import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Send, CheckCircle, AlertTriangle, RefreshCw, Settings, Info, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_BADGE = {
  sent: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
  mock: 'bg-slate-500/15 text-slate-300 border border-slate-500/30',
  failed: 'bg-rose-500/15 text-rose-300 border border-rose-500/30',
  render_error: 'bg-rose-500/15 text-rose-300 border border-rose-500/30',
  queued: 'bg-amber-500/15 text-amber-300 border border-amber-500/30',
};

const TYPE_LABELS = {
  welcome: 'Welcome',
  team_invite: 'Team Invite',
  password_reset: 'Password Reset',
  demo_portal_credentials: 'Demo Credentials',
  interview_scheduled: 'Interview',
  support_ticket_reply: 'Support Reply',
  payment_success: 'Payment Success',
  payment_failed: 'Payment Failed',
  subscription_expiring: 'Sub. Expiring',
  subscription_expired: 'Sub. Expired',
  account_suspended: 'Suspended',
  account_activated: 'Activated',
  test_email: 'Test Email',
};

export default function SuperAdminEmailSettings() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken') || ''}` });

  const refresh = async () => {
    setLoading(true);
    try {
      const [c, s, h] = await Promise.all([
        axios.get(`${API}/notifications/config`, { headers: getHeaders() }),
        axios.get(`${API}/notifications/stats`, { headers: getHeaders() }),
        axios.get(`${API}/notifications/history?limit=30`, { headers: getHeaders() }),
      ]);
      setConfig(c.data);
      setStats(s.data);
      setHistory(h.data);
    } catch {
      toast.error('Failed to load email config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const handleTestSend = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/notifications/test`,
        { recipient_email: testEmail },
        { headers: getHeaders() }
      );
      const { status, message } = res.data || {};
      if (status === 'sent') toast.success(`Test email delivered to ${testEmail}`);
      else if (status === 'mock') toast.info('Test email logged (mock mode — set EMAIL_PROVIDER + EMAIL_API_KEY to send for real)');
      else if (status === 'failed') toast.error(message || 'Send failed');
      else toast.info(message || 'Sent');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="email-settings-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Email Notifications
          </h1>
          <p className="text-sm text-slate-400 mt-1">Transactional email delivery — provider, test send, and audit log.</p>
        </div>
        <Button variant="outline" onClick={refresh} disabled={loading} className="border-slate-700 text-slate-200" data-testid="refresh-email-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Setup Guide CTA — prominent when in mock mode */}
      {config && !config.live_sending_enabled && (
        <button
          onClick={() => navigate('/super-admin/email/setup-guide')}
          data-testid="setup-guide-cta"
          className="w-full text-left bg-gradient-to-br from-violet-500/10 to-indigo-500/10 border border-violet-500/40 rounded-lg p-5 hover:border-violet-400 transition-all group"
        >
          <div className="flex items-start gap-4">
            <div className="bg-violet-500/20 p-3 rounded-lg flex-shrink-0">
              <BookOpen className="h-6 w-6 text-violet-300" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-white text-base group-hover:text-violet-200 transition-colors">
                First time? Follow the Email Setup Guide →
              </h3>
              <p className="text-sm text-slate-300 mt-1">
                Step-by-step walkthrough for Resend, SendGrid, or ZeptoMail — including DKIM, SPF, and DMARC DNS records.
                Takes about 15 minutes start-to-finish.
              </p>
            </div>
          </div>
        </button>
      )}

      {/* Config status */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-5" data-testid="email-config-card">
        <div className="flex items-center gap-2 mb-4">
          <Settings className="h-5 w-5 text-violet-400" />
          <h2 className="font-bold text-white text-base">Provider Configuration</h2>
        </div>
        {!config ? (
          <p className="text-slate-500 text-sm">Loading...</p>
        ) : (
          <>
            <div className={`flex items-start gap-3 p-3 rounded-lg mb-4 ${config.live_sending_enabled ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
              {config.live_sending_enabled ? (
                <CheckCircle className="h-5 w-5 text-emerald-400 mt-0.5" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5" />
              )}
              <div>
                <p className={`text-sm font-semibold ${config.live_sending_enabled ? 'text-emerald-300' : 'text-amber-300'}`}>
                  {config.live_sending_enabled
                    ? `Live delivery active via ${config.provider}`
                    : 'Mock mode — no real emails are being sent'}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {config.live_sending_enabled
                    ? 'All transactional emails (welcome, password reset, demo credentials, interview, support replies, payments) are delivered.'
                    : `Set EMAIL_PROVIDER (resend or sendgrid) and EMAIL_API_KEY in /app/backend/.env, then restart backend to enable live delivery.`}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Provider</p>
                <p className="text-white font-mono">{config.provider}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">API Key</p>
                <p className="text-white font-mono">{config.api_key_configured ? '••• configured' : 'not set'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">From Address</p>
                <p className="text-white font-mono break-all">{config.from_address}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Frontend URL</p>
                <p className="text-white font-mono break-all">{config.frontend_url}</p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Test sender */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-5" data-testid="test-send-card">
        <div className="flex items-center gap-2 mb-4">
          <Send className="h-5 w-5 text-violet-400" />
          <h2 className="font-bold text-white text-base">Send Test Email</h2>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Use this to verify provider configuration after setting up Resend or SendGrid. The test email will be delivered to the address below using the currently-configured provider.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Label className="text-xs text-slate-400">Recipient Email</Label>
            <Input
              type="email"
              data-testid="test-email-input"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              placeholder="you@yourdomain.com"
              className="bg-slate-800 border-slate-700 text-white mt-1"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={handleTestSend}
              disabled={sending || !testEmail}
              className="bg-violet-600 hover:bg-violet-700 text-white font-bold"
              data-testid="send-test-btn"
            >
              {sending ? 'Sending...' : <><Send className="h-4 w-4 mr-2" />Send Test</>}
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Total</p>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Sent</p>
            <p className="text-2xl font-bold text-emerald-400">{stats.by_status?.sent || 0}</p>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Mock (not delivered)</p>
            <p className="text-2xl font-bold text-slate-300">{stats.by_status?.mock || 0}</p>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Failed</p>
            <p className="text-2xl font-bold text-rose-400">{stats.by_status?.failed || 0}</p>
          </div>
        </div>
      )}

      {/* History */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-5" data-testid="email-history-card">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="h-5 w-5 text-violet-400" />
          <h2 className="font-bold text-white text-base">Recent Notifications</h2>
        </div>
        {history.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm flex flex-col items-center gap-2">
            <Info className="h-6 w-6 text-slate-600" />
            No notifications yet — trigger a user creation, password reset, or send a test email to see activity here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-800">
                  <th className="py-2 font-medium">Type</th>
                  <th className="py-2 font-medium">Recipient</th>
                  <th className="py-2 font-medium">Subject</th>
                  <th className="py-2 font-medium">Status</th>
                  <th className="py-2 font-medium">When</th>
                </tr>
              </thead>
              <tbody>
                {history.map(n => (
                  <tr key={n.id} className="border-b border-slate-800/60" data-testid={`email-row-${n.id}`}>
                    <td className="py-2 text-slate-300">{TYPE_LABELS[n.email_type] || n.email_type}</td>
                    <td className="py-2 text-slate-400 break-all">{n.recipient_email}</td>
                    <td className="py-2 text-slate-400 truncate max-w-xs">{n.subject}</td>
                    <td className="py-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_BADGE[n.status] || 'bg-slate-700 text-slate-300'}`}>
                        {n.status}
                      </span>
                    </td>
                    <td className="py-2 text-slate-500 text-xs whitespace-nowrap">
                      {n.created_at ? new Date(n.created_at).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
